import * as ramda from 'ramda';

import type {ComparedActivityReportSummary, ReportRowData} from 'nutshell-core/types';
import {getSortFunc} from 'nutshell-core/utils/sorting/get-sort-func';

import type {SortParams} from '../report-types';

import {USER_SORT_STRING, SORT_DIRECTION_ASCENDING} from './constants';

export function sortSummaryData(
    data?: ComparedActivityReportSummary<ReportRowData[]> | null,
    sort?: SortParams | null
) {
    if (!sort || !data) return data;

    // Create a map of users to activity counts for the specified activity type.
    // Only current period data is used, because that is what we will want to sort by.
    const initial: {
        [userName: string]: number | string;
    } = {};
    const currentActivityCounts = data.currentPeriod.reduce((accum, user) => {
        if (sort && sort.by === USER_SORT_STRING) {
            accum[user.title] = user.title;
        } else if (user.segments) {
            const activitySegment = user.segments.find((seg) => seg.title === (sort && sort.by));
            if (activitySegment) {
                const totals = activitySegment.totalsByReport.find(
                    (report) => report.type === 'activityTypes'
                );
                // @ts-expect-error
                accum[user.title] = totals ? totals.count.value : 0;
            }
        } else {
            accum[user.title] = 0;
        }

        return accum;
    }, initial);

    const sortAscending = sort.direction === SORT_DIRECTION_ASCENDING;
    const sortByUser = sort && sort.by === USER_SORT_STRING;
    const sortFunc = getSortFunc(sortAscending, sortByUser);

    // This function will sort a ReportRowData based on the activity counts per user
    // determined above in currentActivityCounts
    const sortByType = ramda.sort(
        sortFunc((dataPoint: ReportRowData) =>
            sortByUser && typeof currentActivityCounts[dataPoint.title] === 'string'
                ? // @ts-expect-error
                  currentActivityCounts[dataPoint.title].toLowerCase()
                : currentActivityCounts[dataPoint.title]
        )
    );

    const sorted = {
        ...data,
        currentPeriod: sortByType(data.currentPeriod),
    } as const;

    // Only add a compareTo key if the original report had one
    if (data.compareTo) {
        // @ts-expect-error
        sorted.compareTo = sortByType(data.compareTo);
    }

    return sorted;
}

/**
 * Given the activity data and a sort string, determine if that data can be
 * sorted by the sort string.
 *
 * @param  {Object} data   The activity report data
 * @param  {string} sortBy The activity type to sort by, or `User` to sort by user's name``
 * @return {boolean}       True if data can be sorted by the given sort, false otherwise
 */
export function checkDataCanBeSortedByValue(
    data: ComparedActivityReportSummary<Array<ReportRowData>> | null | undefined,
    sortBy: string
): boolean {
    if (!data) {
        // No data, so say we contain the current sort
        return true;
    }

    if (sortBy === USER_SORT_STRING) {
        // If sorting by the user name, we just know that the data contains it
        return true;
    }

    if (data.currentPeriod.length === 0) {
        // No users, so say we contain the current sort
        return true;
    }

    // @ts-expect-error
    if (!data.currentPeriod[0].segments) {
        return true;
    }

    // Return true if current period contains the activity to sort by
    return Boolean(
        // @ts-expect-error
        data.currentPeriod[0].segments.find((activity) => {
            return activity.title === sortBy;
        })
    );
}
